import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Truck, ShieldCheck, Smile } from "lucide-react";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import CategoryRow from "../components/CategoryRow";
import FaqSection from "../components/FaqSection";
import { useSiteSettings } from "../hooks/useSiteSettings";

const DEFAULT_HERO = "https://images.unsplash.com/photo-1582058091505-f87a2e55a40f?auto=format&fit=crop&q=80&w=900";

export default function Home() {
    const [featured, setFeatured] = useState([]);
    const [categories, setCategories] = useState([]);
    const { settings } = useSiteSettings();
    const heroImage = settings?.hero_image || DEFAULT_HERO;

    useEffect(() => {
        api.get("/products/featured").then(({ data }) => setFeatured(data)).catch(() => {});
        api.get("/categories").then(({ data }) => {
            setCategories(data.items || []);
        }).catch(() => {});
    }, []);

    return (
        <div data-testid="home-page">
            {/* Hero — compact, banderoso */}
            <section className="relative overflow-hidden bg-mexyellow">
                <div className="absolute inset-0 papel-picado opacity-20" />
                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12 grid md:grid-cols-2 gap-6 md:gap-10 items-center">
                    <div className="space-y-4 animate-fade-up">
                        <span className="inline-flex items-center gap-2 bg-flagred text-white px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-widest shadow-md">
                            <Sparkles className="w-3 h-3" /> 🎁 Sobre $20.000 = regalo gratis
                        </span>
                        <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight">
                            <span className="text-flagred">Antojitos</span>{" "}
                            <span className="text-cocoa">mexicanos</span>{" "}
                            <span className="block bg-flagred text-white px-3 py-1 rounded-xl mt-1 inline-block shadow">
                                que sí pican rico.
                            </span>
                        </h1>
                        <p className="text-sm sm:text-base text-cocoa/85 max-w-md font-medium">
                            Lucas, Vero, De la Rosa, Tajín, Valentina y más. Recibe en 24-48 hrs y suma puntos en cada pedido.
                            <strong className="text-flagred"> ¡No te quedes con las ganas!</strong>
                        </p>
                        <div className="flex flex-wrap gap-2.5">
                            <Link
                                to="/productos"
                                data-testid="hero-cta-products"
                                className="inline-flex items-center gap-2 bg-flagred hover:bg-mexred_hover text-white font-semibold px-5 py-2.5 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all text-sm"
                            >
                                Quiero mis dulces <ArrowRight className="w-4 h-4" />
                            </Link>
                            <Link
                                to="/promociones"
                                className="inline-flex items-center gap-2 bg-white text-cocoa font-semibold px-5 py-2.5 rounded-full hover:bg-flaggreen hover:text-white transition-all text-sm shadow-md border border-cocoa/10"
                            >
                                Ofertas picosas 🔥
                            </Link>
                        </div>
                    </div>
                    <div className="relative hidden md:block animate-fade-up" style={{ animationDelay: "0.15s" }}>
                        <div className="relative aspect-square max-w-sm mx-auto">
                            <div className="absolute inset-0 bg-white rounded-[3rem] rotate-3 shadow-xl" />
                            <img
                                src={heroImage}
                                alt="Dulces mexicanos"
                                className="relative w-full h-full object-cover rounded-[3rem] shadow-2xl"
                            />
                            <div className="absolute -bottom-3 -left-3 bg-flagred text-white px-3 py-1.5 rounded-2xl shadow-lg font-display font-semibold text-sm">
                                ¡Picosísimo!
                            </div>
                            <div className="absolute -top-3 -right-3 bg-flaggreen text-white px-3 py-1.5 rounded-2xl shadow-lg font-display font-semibold text-sm animate-wobble">
                                🎁 +$20.000
                            </div>
                        </div>
                    </div>
                </div>
                <div className="fiesta-stripes h-2 w-full" />
            </section>

            {/* Trust badges */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {[
                        { icon: Truck, title: "Despacho a todo Chile", text: "Envíos rápidos y seguros" },
                        { icon: ShieldCheck, title: "Pago seguro", text: "Comprobante validado por nuestro equipo" },
                        { icon: Smile, title: "Calidad garantizada", text: "Productos originales importados" },
                    ].map((b, i) => (
                        <div
                            key={i}
                            className="flex items-center gap-3 bg-white border border-border rounded-2xl p-4 shadow-sm"
                        >
                            <div className="w-12 h-12 rounded-2xl bg-mexyellow flex items-center justify-center">
                                <b.icon className="w-6 h-6 text-cocoa" />
                            </div>
                            <div>
                                <div className="font-display font-semibold text-cocoa">{b.title}</div>
                                <div className="text-sm text-muted-foreground">{b.text}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Featured */}
            {featured.length > 0 && (
                <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-end justify-between mb-4">
                        <div>
                            <span className="text-flagred font-semibold uppercase tracking-widest text-xs">⭐ Lo más vendido</span>
                            <h2 className="font-display text-2xl md:text-3xl font-semibold text-cocoa mt-1">
                                Destacados de la dulcería
                            </h2>
                        </div>
                        <Link to="/productos" className="text-flagred font-semibold text-sm hover:underline hidden sm:inline">
                            Ver todo →
                        </Link>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                        {featured.slice(0, 8).map((p) => (
                            <ProductCard key={p.id} product={p} />
                        ))}
                    </div>
                </section>
            )}

            {/* Per-category carousels */}
            <div className="bg-cream py-4">
                {categories.map((c) => (
                    <CategoryRow key={c.id || c.name} category={c.name} emoji={c.emoji} />
                ))}
            </div>

            {/* Promo banner */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                <div className="relative rounded-3xl overflow-hidden bg-cocoa text-white p-8 md:p-12">
                    <div className="absolute inset-0 fiesta-stripes opacity-10" />
                    <div className="relative grid md:grid-cols-2 gap-6 items-center">
                        <div>
                            <span className="text-mexyellow font-semibold uppercase tracking-widest text-xs">🎁 Programa de puntos + regalo sorpresa</span>
                            <h3 className="font-display text-3xl md:text-4xl font-semibold mt-2 mb-3">
                                Cada pedido suma. ¡No dejes plata sobre la mesa!
                            </h3>
                            <p className="text-white/80 mb-4">
                                Por cada $100 CLP gastados, sumas 1 punto canjeable por descuentos. Y si tu pedido supera $20.000, va un <strong className="text-mexyellow">regalo sorpresa GRATIS</strong> en tu paquete 🎉.
                            </p>
                            <Link
                                to="/registro"
                                className="inline-flex items-center gap-2 bg-mexyellow text-cocoa font-semibold px-6 py-3 rounded-full hover:bg-mexyellow/90 transition-all shadow-md"
                            >
                                Crear cuenta y empezar a sumar <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                        <div className="hidden md:block text-right">
                            <span className="font-display text-7xl">🌶️🍬🍫</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <FaqSection />
        </div>
    );
}
